import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  BarChart3, 
  PieChart as PieChartIcon,
  RefreshCw,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Wallet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ComposedChart
} from 'recharts';

// Datos del presupuesto 2025 extraídos del Excel
const PRESUPUESTO_2025 = {
  ingresos: {
    cuotas: {
      activos: [55000000, 60000000, 90000000, 75000000, 70000000, 68000000, 65000000, 65000000, 80000000, 72000000, 60000000, 46800000],
      adherentes: [6000000, 6500000, 9000000, 7500000, 7000000, 6800000, 6500000, 6500000, 8000000, 7200000, 6000000, 4800000],
      institucionales: [0, 0, 5250000, 5250000, 5250000, 5250000, 5250000, 5250000, 5250000, 5250000, 5250000, 2625000],
      interior: [6500000, 7000000, 10000000, 8500000, 8000000, 7800000, 7500000, 7500000, 9000000, 8000000, 7000000, 5600000],
      subtotal: [67500000, 73500000, 114250000, 96250000, 90250000, 87850000, 84250000, 84250000, 102250000, 92450000, 78250000, 59825000],
      total: 1030875000
    },
    certificaciones: {
      inicial: [1900000, 1900000, 3800000, 3800000, 3325000, 2375000, 1900000, 1900000, 3800000, 3800000, 2375000, 1900000],
      recertificacion: [2275000, 2275000, 4550000, 4550000, 4550000, 2275000, 2275000, 2275000, 4550000, 4550000, 2275000, 2275000],
      subespecialidad: [950000, 950000, 1900000, 1900000, 1425000, 950000, 950000, 1425000, 1900000, 1900000, 1425000, 950000],
      subtotal: [5125000, 5125000, 10250000, 10250000, 9300000, 5600000, 5125000, 5600000, 10250000, 10250000, 6075000, 5125000],
      total: 88075000
    },
    formacion: {
      online: [4200000, 4200000, 6300000, 5600000, 5250000, 5250000, 4900000, 4900000, 6300000, 5600000, 2800000, 1400000],
      presencial: [0, 2250000, 4500000, 4500000, 4500000, 2250000, 2250000, 2250000, 4500000, 4500000, 2250000, 0],
      posgrado: [1200000, 1200000, 2400000, 2400000, 2400000, 1200000, 1200000, 1200000, 2400000, 2400000, 1200000, 0],
      subtotal: [5400000, 7650000, 13200000, 12500000, 12150000, 8700000, 8350000, 8350000, 13200000, 12500000, 6250000, 1400000],
      total: 109650000
    },
    eventos: {
      jornadas: [0, 0, 2500000, 0, 2500000, 0, 2500000, 0, 2500000, 0, 0, 0],
      subtotal: [0, 0, 2500000, 0, 2500000, 0, 2500000, 0, 2500000, 0, 0, 0],
      total: 10000000
    },
    otros: {
      libros: [300000, 300000, 600000, 600000, 450000, 300000, 300000, 300000, 600000, 600000, 450000, 300000],
      revista: [0, 1500000, 0, 1500000, 0, 1500000, 0, 1500000, 0, 0, 0, 0],
      sponsors: [1000000, 1000000, 2000000, 1500000, 1500000, 1500000, 1000000, 1000000, 1500000, 1500000, 1000000, 500000],
      intereses: [250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000],
      subsidios: [0, 0, 500000, 500000, 500000, 0, 0, 0, 500000, 0, 0, 0],
      subtotal: [1550000, 3050000, 3350000, 4350000, 2700000, 3550000, 1550000, 3050000, 2850000, 2350000, 1700000, 1050000],
      total: 31100000
    },
    totalMensual: [79575000, 89325000, 143550000, 123350000, 116900000, 105700000, 101775000, 101250000, 131050000, 117550000, 92275000, 67400000],
    totalAnual: 1269700000
  },
  egresos: {
    personal: {
      sueldos: [3600000, 3600000, 3600000, 3600000, 3600000, 3600000, 4320000, 3600000, 3600000, 3600000, 3600000, 4320000],
      bonos: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1800000],
      capacitacion: [0, 0, 300000, 0, 0, 300000, 0, 0, 300000, 0, 0, 300000],
      subtotal: [3600000, 3600000, 3900000, 3600000, 3600000, 3900000, 4320000, 3600000, 3900000, 3600000, 3600000, 6420000],
      total: 47640000
    },
    sede: {
      alquiler: [1800000, 1800000, 1800000, 1800000, 1800000, 1800000, 1800000, 1800000, 1800000, 1800000, 1800000, 1800000],
      servicios: [300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000],
      mantenimiento: [0, 0, 600000, 0, 0, 600000, 0, 0, 600000, 0, 0, 600000],
      seguros: [250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000, 250000],
      subtotal: [2350000, 2350000, 2950000, 2350000, 2350000, 2950000, 2350000, 2350000, 2950000, 2350000, 2350000, 2950000],
      total: 30600000
    },
    tecnologia: {
      desarrollo: [1500000, 1500000, 1500000, 1500000, 1500000, 1500000, 1500000, 1500000, 1500000, 1500000, 1500000, 1500000],
      hosting: [300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000],
      licencias: [200000, 200000, 200000, 200000, 200000, 200000, 200000, 200000, 200000, 200000, 200000, 200000],
      subtotal: [2000000, 2000000, 2000000, 2000000, 2000000, 2000000, 2000000, 2000000, 2000000, 2000000, 2000000, 2000000],
      total: 24000000
    },
    eventos: {
      jornadas: [0, 0, 1200000, 0, 1200000, 0, 1200000, 0, 1200000, 0, 0, 0],
      webinars: [0, 0, 200000, 0, 200000, 0, 200000, 0, 200000, 0, 0, 0],
      subtotal: [0, 0, 1400000, 0, 1400000, 0, 1400000, 0, 1400000, 0, 0, 0],
      total: 5600000
    },
    comunicacion: {
      marketing: [400000, 400000, 400000, 400000, 400000, 400000, 400000, 400000, 400000, 400000, 400000, 400000],
      revista: [0, 400000, 0, 400000, 0, 400000, 0, 400000, 0, 0, 0, 0],
      libros: [0, 0, 800000, 0, 0, 0, 800000, 0, 0, 0, 0, 0],
      subtotal: [400000, 800000, 1200000, 800000, 400000, 800000, 1200000, 800000, 400000, 400000, 400000, 400000],
      total: 8000000
    },
    profesionales: {
      contables: [700000, 700000, 700000, 700000, 700000, 700000, 700000, 700000, 700000, 700000, 700000, 700000],
      legales: [300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000, 300000],
      subtotal: [1000000, 1000000, 1000000, 1000000, 1000000, 1000000, 1000000, 1000000, 1000000, 1000000, 1000000, 1000000],
      total: 12000000
    },
    totalMensual: [9350000, 9750000, 12450000, 9750000, 10750000, 10650000, 12270000, 9750000, 11650000, 9350000, 9350000, 12770000],
    totalAnual: 127840000
  },
  resultados: {
    mensual: [],
    totalAnual: 0
  }
};

// Calcular resultados
PRESUPUESTO_2025.resultados.mensual = PRESUPUESTO_2025.ingresos.totalMensual.map((ing, i) => 
  ing - PRESUPUESTO_2025.egresos.totalMensual[i]
);
PRESUPUESTO_2025.resultados.totalAnual = PRESUPUESTO_2025.ingresos.totalAnual - PRESUPUESTO_2025.egresos.totalAnual;

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Colores para gráficos
const COLORS = {
  primary: '#2853B9',
  secondary: '#1E3876',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  purple: '#8B5CF6',
  pink: '#EC4899',
  orange: '#F97316',
  teal: '#14B8A6'
};

const PIE_COLORS = [COLORS.primary, COLORS.success, COLORS.purple, COLORS.warning, COLORS.pink, COLORS.teal];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0
  }).format(value);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(value);
};

const formatMillions = (value) => {
  return `$${(value / 1000000).toFixed(0)}M`;
};

// Tooltip personalizado para gráficos
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-xl border border-gray-100">
        <p className="font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Loading Modal Component
const LoadingModal = ({ isOpen }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
        >
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-6">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2853B9] to-[#1E3876] flex items-center justify-center">
                <RefreshCw className="w-10 h-10 text-white animate-spin" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-4 border-white">
                <BarChart3 className="w-4 h-4 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Recuperando información
            </h3>
            <p className="text-gray-600 mb-4">
              Conectando con ERP, Backoffice, Mercado...
            </p>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span>Sincronizando datos financieros</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "blue" }) => {
  const colorClasses = {
    blue: "from-[#2853B9] to-[#1E3876]",
    green: "from-green-500 to-green-600",
    red: "from-red-500 to-red-600",
    purple: "from-purple-500 to-purple-600"
  };

  return (
    <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow">
      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${colorClasses[color]} opacity-10 rounded-full transform translate-x-8 -translate-y-8`}></div>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          {trend && (
            <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-gray-500 mb-1">{title}</h3>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

// Data Table Component
const DataTable = ({ title, headers, rows, highlightLast = false }) => (
  <Card className="border-0 shadow-lg">
    <CardHeader className="border-b bg-gray-50/50">
      <CardTitle className="text-lg">{title}</CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left p-4 font-semibold text-gray-700 sticky left-0 bg-gray-50">{headers[0]}</th>
              {headers.slice(1).map((h, i) => (
                <th key={i} className="text-right p-4 font-semibold text-gray-700 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr 
                key={i} 
                className={`border-b hover:bg-gray-50 transition-colors ${
                  highlightLast && i === rows.length - 1 ? 'bg-[#2853B9]/5 font-semibold' : ''
                }`}
              >
                <td className="p-4 text-gray-900 sticky left-0 bg-white">{row[0]}</td>
                {row.slice(1).map((cell, j) => (
                  <td key={j} className="text-right p-4 text-gray-700 whitespace-nowrap">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

const PresupuestoFinanciero = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("resumen");

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  // Datos para gráficos mensuales
  const monthlyData = MESES.map((mes, i) => ({
    name: mes,
    ingresos: PRESUPUESTO_2025.ingresos.totalMensual[i],
    egresos: PRESUPUESTO_2025.egresos.totalMensual[i],
    resultado: PRESUPUESTO_2025.resultados.mensual[i]
  }));

  // Datos para pie chart de ingresos
  const ingresosDistribucion = [
    { name: 'Cuotas de Socios', value: PRESUPUESTO_2025.ingresos.cuotas.total },
    { name: 'Certificaciones', value: PRESUPUESTO_2025.ingresos.certificaciones.total },
    { name: 'Formación', value: PRESUPUESTO_2025.ingresos.formacion.total },
    { name: 'Eventos', value: PRESUPUESTO_2025.ingresos.eventos.total },
    { name: 'Otros', value: PRESUPUESTO_2025.ingresos.otros.total }
  ];

  // Datos para pie chart de egresos
  const egresosDistribucion = [
    { name: 'Personal', value: PRESUPUESTO_2025.egresos.personal.total },
    { name: 'Sede', value: PRESUPUESTO_2025.egresos.sede.total },
    { name: 'Tecnología', value: PRESUPUESTO_2025.egresos.tecnologia.total },
    { name: 'Eventos', value: PRESUPUESTO_2025.egresos.eventos.total },
    { name: 'Comunicación', value: PRESUPUESTO_2025.egresos.comunicacion.total },
    { name: 'Profesionales', value: PRESUPUESTO_2025.egresos.profesionales.total }
  ];

  // Datos detallados de ingresos por categoría
  const ingresosDetallados = MESES.map((mes, i) => ({
    name: mes,
    cuotas: PRESUPUESTO_2025.ingresos.cuotas.subtotal[i],
    certificaciones: PRESUPUESTO_2025.ingresos.certificaciones.subtotal[i],
    formacion: PRESUPUESTO_2025.ingresos.formacion.subtotal[i],
    eventos: PRESUPUESTO_2025.ingresos.eventos.subtotal[i],
    otros: PRESUPUESTO_2025.ingresos.otros.subtotal[i]
  }));

  // Datos detallados de egresos por categoría
  const egresosDetallados = MESES.map((mes, i) => ({
    name: mes,
    personal: PRESUPUESTO_2025.egresos.personal.subtotal[i],
    sede: PRESUPUESTO_2025.egresos.sede.subtotal[i],
    tecnologia: PRESUPUESTO_2025.egresos.tecnologia.subtotal[i],
    eventos: PRESUPUESTO_2025.egresos.eventos.subtotal[i],
    comunicacion: PRESUPUESTO_2025.egresos.comunicacion.subtotal[i],
    profesionales: PRESUPUESTO_2025.egresos.profesionales.subtotal[i]
  }));

  // Preparar datos para las tablas
  const ingresosRows = [
    ['Socios Activos', ...PRESUPUESTO_2025.ingresos.cuotas.activos.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.cuotas.activos.reduce((a,b) => a+b, 0))],
    ['Socios Adherentes', ...PRESUPUESTO_2025.ingresos.cuotas.adherentes.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.cuotas.adherentes.reduce((a,b) => a+b, 0))],
    ['Socios Institucionales', ...PRESUPUESTO_2025.ingresos.cuotas.institucionales.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.cuotas.institucionales.reduce((a,b) => a+b, 0))],
    ['Socios del Interior', ...PRESUPUESTO_2025.ingresos.cuotas.interior.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.cuotas.interior.reduce((a,b) => a+b, 0))],
    ['SUBTOTAL CUOTAS', ...PRESUPUESTO_2025.ingresos.cuotas.subtotal.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.cuotas.total)],
    ['Certificación Inicial', ...PRESUPUESTO_2025.ingresos.certificaciones.inicial.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.certificaciones.inicial.reduce((a,b) => a+b, 0))],
    ['Recertificación', ...PRESUPUESTO_2025.ingresos.certificaciones.recertificacion.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.certificaciones.recertificacion.reduce((a,b) => a+b, 0))],
    ['Subespecialidades', ...PRESUPUESTO_2025.ingresos.certificaciones.subespecialidad.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.certificaciones.subespecialidad.reduce((a,b) => a+b, 0))],
    ['SUBTOTAL CERTIFICACIONES', ...PRESUPUESTO_2025.ingresos.certificaciones.subtotal.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.certificaciones.total)],
    ['Cursos Online', ...PRESUPUESTO_2025.ingresos.formacion.online.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.formacion.online.reduce((a,b) => a+b, 0))],
    ['Cursos Presenciales', ...PRESUPUESTO_2025.ingresos.formacion.presencial.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.formacion.presencial.reduce((a,b) => a+b, 0))],
    ['Maestría/Posgrado', ...PRESUPUESTO_2025.ingresos.formacion.posgrado.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.formacion.posgrado.reduce((a,b) => a+b, 0))],
    ['SUBTOTAL FORMACIÓN', ...PRESUPUESTO_2025.ingresos.formacion.subtotal.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.formacion.total)],
    ['TOTAL INGRESOS', ...PRESUPUESTO_2025.ingresos.totalMensual.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.totalAnual)],
  ];

  const egresosRows = [
    ['Sueldos y Cargas', ...PRESUPUESTO_2025.egresos.personal.sueldos.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.personal.sueldos.reduce((a,b) => a+b, 0))],
    ['Bonos', ...PRESUPUESTO_2025.egresos.personal.bonos.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.personal.bonos.reduce((a,b) => a+b, 0))],
    ['Capacitación', ...PRESUPUESTO_2025.egresos.personal.capacitacion.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.personal.capacitacion.reduce((a,b) => a+b, 0))],
    ['SUBTOTAL PERSONAL', ...PRESUPUESTO_2025.egresos.personal.subtotal.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.personal.total)],
    ['Alquiler Sede', ...PRESUPUESTO_2025.egresos.sede.alquiler.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.sede.alquiler.reduce((a,b) => a+b, 0))],
    ['Servicios', ...PRESUPUESTO_2025.egresos.sede.servicios.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.sede.servicios.reduce((a,b) => a+b, 0))],
    ['Mantenimiento', ...PRESUPUESTO_2025.egresos.sede.mantenimiento.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.sede.mantenimiento.reduce((a,b) => a+b, 0))],
    ['SUBTOTAL SEDE', ...PRESUPUESTO_2025.egresos.sede.subtotal.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.sede.total)],
    ['CAO Digital', ...PRESUPUESTO_2025.egresos.tecnologia.desarrollo.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.tecnologia.desarrollo.reduce((a,b) => a+b, 0))],
    ['Hosting/Cloud', ...PRESUPUESTO_2025.egresos.tecnologia.hosting.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.tecnologia.hosting.reduce((a,b) => a+b, 0))],
    ['Licencias', ...PRESUPUESTO_2025.egresos.tecnologia.licencias.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.tecnologia.licencias.reduce((a,b) => a+b, 0))],
    ['SUBTOTAL TECNOLOGÍA', ...PRESUPUESTO_2025.egresos.tecnologia.subtotal.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.tecnologia.total)],
    ['TOTAL EGRESOS', ...PRESUPUESTO_2025.egresos.totalMensual.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.totalAnual)],
  ];

  const resultadosRows = [
    ['Ingresos', ...PRESUPUESTO_2025.ingresos.totalMensual.map(formatNumber), formatNumber(PRESUPUESTO_2025.ingresos.totalAnual)],
    ['Egresos', ...PRESUPUESTO_2025.egresos.totalMensual.map(formatNumber), formatNumber(PRESUPUESTO_2025.egresos.totalAnual)],
    ['RESULTADO', ...PRESUPUESTO_2025.resultados.mensual.map(formatNumber), formatNumber(PRESUPUESTO_2025.resultados.totalAnual)],
  ];

  return (
    <div className="space-y-6" data-testid="presupuesto-financiero-page">
      <LoadingModal isOpen={loading} />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Presupuesto 2025</h1>
          <p className="text-gray-600 mt-1">Consejo Argentino de Oftalmología</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-white px-4 py-2 rounded-lg shadow-sm">
          <Calendar className="w-4 h-4" />
          <span>Última actualización: {new Date().toLocaleDateString('es-AR')}</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Ingresos Totales"
          value={formatCurrency(PRESUPUESTO_2025.ingresos.totalAnual)}
          subtitle="Proyección anual 2025"
          icon={TrendingUp}
          trend="up"
          trendValue="+80% vs 2024"
          color="green"
        />
        <KPICard
          title="Egresos Totales"
          value={formatCurrency(PRESUPUESTO_2025.egresos.totalAnual)}
          subtitle="Presupuesto anual"
          icon={TrendingDown}
          color="red"
        />
        <KPICard
          title="Resultado Neto"
          value={formatCurrency(PRESUPUESTO_2025.resultados.totalAnual)}
          subtitle="Superávit proyectado"
          icon={Wallet}
          trend="up"
          trendValue="89.9%"
          color="blue"
        />
        <KPICard
          title="Cuotas de Socios"
          value={formatCurrency(PRESUPUESTO_2025.ingresos.cuotas.total)}
          subtitle="81% del total de ingresos"
          icon={Users}
          color="purple"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white shadow-sm border p-1 rounded-lg">
          <TabsTrigger value="resumen" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <PieChartIcon className="w-4 h-4 mr-2" />
            Resumen Ejecutivo
          </TabsTrigger>
          <TabsTrigger value="ingresos" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <TrendingUp className="w-4 h-4 mr-2" />
            Ingresos
          </TabsTrigger>
          <TabsTrigger value="egresos" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <TrendingDown className="w-4 h-4 mr-2" />
            Egresos
          </TabsTrigger>
          <TabsTrigger value="resultados" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <BarChart3 className="w-4 h-4 mr-2" />
            Estado de Resultados
          </TabsTrigger>
        </TabsList>

        {/* Resumen Tab */}
        <TabsContent value="resumen" className="space-y-6">
          {/* Gráfico principal - Evolución mensual */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Evolución Mensual 2025</CardTitle>
              <CardDescription>Comparativo de ingresos, egresos y resultado neto</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatMillions} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="ingresos" name="Ingresos" fill={COLORS.success} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="egresos" name="Egresos" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                  <Line 
                    type="monotone" 
                    dataKey="resultado" 
                    name="Resultado" 
                    stroke={COLORS.primary} 
                    strokeWidth={3}
                    dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Pie Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribución de Ingresos */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Distribución de Ingresos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={ingresosDistribucion}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                    >
                      {ingresosDistribucion.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Distribución de Egresos */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  Distribución de Egresos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={egresosDistribucion}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                    >
                      {egresosDistribucion.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Área chart del resultado acumulado */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Resultado Neto Mensual</CardTitle>
              <CardDescription>Superávit mensual proyectado</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="colorResultado" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatMillions} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="resultado" 
                    name="Resultado Neto"
                    stroke={COLORS.primary} 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorResultado)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ingresos Tab */}
        <TabsContent value="ingresos" className="space-y-6">
          {/* Gráfico de área apilada de ingresos */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Composición de Ingresos por Mes</CardTitle>
              <CardDescription>Desglose por categoría de ingreso</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={ingresosDetallados}>
                  <defs>
                    <linearGradient id="colorCuotas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.2}/>
                    </linearGradient>
                    <linearGradient id="colorCert" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.2}/>
                    </linearGradient>
                    <linearGradient id="colorForm" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0.2}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatMillions} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="cuotas" name="Cuotas" stackId="1" stroke={COLORS.primary} fill="url(#colorCuotas)" />
                  <Area type="monotone" dataKey="certificaciones" name="Certificaciones" stackId="1" stroke={COLORS.success} fill="url(#colorCert)" />
                  <Area type="monotone" dataKey="formacion" name="Formación" stackId="1" stroke={COLORS.purple} fill="url(#colorForm)" />
                  <Area type="monotone" dataKey="eventos" name="Eventos" stackId="1" stroke={COLORS.warning} fill={COLORS.warning} fillOpacity={0.5} />
                  <Area type="monotone" dataKey="otros" name="Otros" stackId="1" stroke={COLORS.pink} fill={COLORS.pink} fillOpacity={0.5} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla de ingresos */}
          <DataTable
            title="Presupuesto de Ingresos 2025 (ARS)"
            headers={['Concepto', ...MESES, 'Total']}
            rows={ingresosRows}
            highlightLast
          />
        </TabsContent>

        {/* Egresos Tab */}
        <TabsContent value="egresos" className="space-y-6">
          {/* Gráfico de barras apiladas de egresos */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Composición de Egresos por Mes</CardTitle>
              <CardDescription>Desglose por categoría de gasto</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={egresosDetallados}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatMillions} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="personal" name="Personal" stackId="a" fill={COLORS.danger} />
                  <Bar dataKey="sede" name="Sede" stackId="a" fill={COLORS.orange} />
                  <Bar dataKey="tecnologia" name="Tecnología" stackId="a" fill={COLORS.primary} />
                  <Bar dataKey="eventos" name="Eventos" stackId="a" fill={COLORS.purple} />
                  <Bar dataKey="comunicacion" name="Comunicación" stackId="a" fill={COLORS.pink} />
                  <Bar dataKey="profesionales" name="Profesionales" stackId="a" fill={COLORS.teal} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla de egresos */}
          <DataTable
            title="Presupuesto de Egresos 2025 (ARS)"
            headers={['Concepto', ...MESES, 'Total']}
            rows={egresosRows}
            highlightLast
          />
        </TabsContent>

        {/* Resultados Tab */}
        <TabsContent value="resultados" className="space-y-6">
          {/* Gráfico de líneas de resultados */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Evolución del Estado de Resultados</CardTitle>
              <CardDescription>Comparativo mensual ingresos vs egresos</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={formatMillions} tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="ingresos" 
                    name="Ingresos" 
                    stroke={COLORS.success} 
                    strokeWidth={3}
                    dot={{ fill: COLORS.success, strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 8 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="egresos" 
                    name="Egresos" 
                    stroke={COLORS.danger} 
                    strokeWidth={3}
                    dot={{ fill: COLORS.danger, strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 8 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="resultado" 
                    name="Resultado" 
                    stroke={COLORS.primary} 
                    strokeWidth={3}
                    strokeDasharray="5 5"
                    dot={{ fill: COLORS.primary, strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 8 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla de resultados */}
          <DataTable
            title="Estado de Resultados 2025 (ARS)"
            headers={['Concepto', ...MESES, 'Total']}
            rows={resultadosRows}
            highlightLast
          />
          
          {/* Card de resultado final */}
          <Card className="border-0 shadow-lg bg-gradient-to-r from-[#2853B9] to-[#1E3876] text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-medium opacity-90">Resultado Neto Proyectado 2025</h3>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(PRESUPUESTO_2025.resultados.totalAnual)}</p>
                </div>
                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-medium">Margen: 89.9%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PresupuestoFinanciero;
